import { useState, useCallback, useEffect } from 'react';
import { SelectItemProps, Response, Account } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { useLazyGetFintracTransactionsAccountsQuery } from '../store/services';
import { setTransactionAccounts } from '../store/slices/list';

interface ListResponse {
  data: Response<Account>;
}

let timeout: NodeJS.Timeout;

const useGetAccountsAutocompleteList = (
  query?: string,
  valueFieldName?: string,
) => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [accountsList, setAccountsList] = useState<Account[]>([]);

  const dispatch = useAppDispatch();

  const { overdraftEnabled, transactionAccounts } = useAppSelector(
    (state) => state.fintrac.list,
  );

  const [getList, { isFetching, isLoading }] =
    useLazyGetFintracTransactionsAccountsQuery();

  const getAutocompleteListAction = useCallback(
    async (search?: string) => {
      const results: SelectItemProps[] = [];
      const accounts: Account[] = [];

      const response = (await getList(
        `?take=40&sortProp=fullNumber&sortDir=asc${
          search ? `&search=${search}` : ''
        }${query || ''}`,
      )) as ListResponse;

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.description,
            description: '',
            value: valueFieldName
              ? (item.node[valueFieldName as keyof Account] as string)
              : item.node.fullNumber,
            suffix: `${item.node.fullNumber} ${
              // eslint-disable-next-line no-nested-ternary
              overdraftEnabled
                ? item?.node?.overdraftEnabled
                  ? '(Overdraft)'
                  : ''
                : ''
            }`,
          });

          accounts.push(item.node);
        });
      }

      setAutocompleteList(results);
      setAccountsList(accounts);

      if (!transactionAccounts) {
        dispatch(setTransactionAccounts(accounts));
      }
    },
    [query, transactionAccounts],
  );

  const onSearch = (search: string) => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      getAutocompleteListAction(search);
    }, 300);
  };

  const onClear = () => {
    getAutocompleteListAction('');
  };

  useEffect(() => {
    if (!transactionAccounts) {
      getAutocompleteListAction();
    } else {
      setAutocompleteList(
        transactionAccounts?.map((item) => ({
          name: item.description,
          description: '',
          value: valueFieldName
            ? (item[valueFieldName as keyof Account] as string)
            : item.fullNumber,
          suffix: `${item.fullNumber} ${
            // eslint-disable-next-line no-nested-ternary
            overdraftEnabled
              ? item?.overdraftEnabled
                ? '(Overdraft)'
                : ''
              : ''
          }`,
        })),
      );

      setAccountsList(transactionAccounts);
    }
  }, [getAutocompleteListAction, transactionAccounts, valueFieldName]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    onSearch,
    isLoading: isLoading || isFetching,
    accountsList,
    onClear,
    setAutocompleteList,
    setAccountsList,
  };
};

export default useGetAccountsAutocompleteList;
